/**
 * Service de gestion des statuts clients pour OMAD SERVICES
 * 
 * Gère la conversion prospect → client basée sur l'acceptation de devis
 */

import { doc, updateDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

// Statuts clients étendus
export const CLIENT_STATUS = {
  PROSPECT: 'prospect',
  CLIENT_ACTIF: 'client_actif', 
  CLIENT_INACTIF: 'client_inactif'
};

// Labels d'affichage des statuts
export const CLIENT_STATUS_LABELS = {
  [CLIENT_STATUS.PROSPECT]: 'Prospect',
  [CLIENT_STATUS.CLIENT_ACTIF]: 'Client actif',
  [CLIENT_STATUS.CLIENT_INACTIF]: 'Client inactif'
};

// Couleurs associées aux statuts
export const CLIENT_STATUS_COLORS = {
  [CLIENT_STATUS.PROSPECT]: 'bg-yellow-100 text-yellow-800',
  [CLIENT_STATUS.CLIENT_ACTIF]: 'bg-green-100 text-green-800',
  [CLIENT_STATUS.CLIENT_INACTIF]: 'bg-gray-100 text-gray-800'
};

/**
 * Convertit l'ancien système isActive vers le nouveau système de statuts
 * @param {boolean} isActive - Ancien statut isActive
 * @param {boolean} hasAcceptedQuote - Si le client a accepté un devis
 * @returns {string} Nouveau statut
 */
export const convertirAncienStatut = (isActive, hasAcceptedQuote = false) => {
  if (hasAcceptedQuote) {
    return isActive ? CLIENT_STATUS.CLIENT_ACTIF : CLIENT_STATUS.CLIENT_INACTIF;
  }
  return CLIENT_STATUS.PROSPECT;
};

/**
 * Détermine le statut d'un client basé sur ses devis
 * @param {Object} db - Instance Firestore
 * @param {string} clientId - ID du client
 * @returns {Promise<Object>} Statut et informations
 */
export const determinerStatutClient = async (db, clientId) => {
  try {
    // Récupérer les devis du client
    const devisQuery = query(
      collection(db, 'devis'),
      where('clientId', '==', clientId)
    );
    const devisSnapshot = await getDocs(devisQuery);
    const devis = devisSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Vérifier s'il y a des devis acceptés
    const devisAcceptes = devis.filter(d => d.statut === 'accepte');
    const hasAcceptedQuote = devisAcceptes.length > 0;

    // Récupérer les factures pour vérifier les contrats actifs
    const facturesQuery = query(
      collection(db, 'factures'),
      where('clientId', '==', clientId)
    );
    const facturesSnapshot = await getDocs(facturesQuery);
    const factures = facturesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Déterminer le statut recommandé
    let statutRecommande;
    if (!hasAcceptedQuote) {
      statutRecommande = CLIENT_STATUS.PROSPECT;
    } else {
      // Si le client a des factures récentes, il est actif
      const maintenant = new Date();
      const ilYa6Mois = new Date(maintenant.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
      
      const facturesRecentes = factures.filter(f => 
        f.dateEmission && f.dateEmission.toDate() > ilYa6Mois
      );

      statutRecommande = facturesRecentes.length > 0 ? 
        CLIENT_STATUS.CLIENT_ACTIF : CLIENT_STATUS.CLIENT_INACTIF;
    }

    return {
      statutRecommande,
      hasAcceptedQuote,
      nombreDevis: devis.length,
      nombreDevisAcceptes: devisAcceptes.length,
      nombreFactures: factures.length,
      derniereActivite: factures.length > 0 ? 
        factures.reduce((latest, f) => 
          f.dateEmission && (!latest || f.dateEmission.toDate() > latest) ? 
          f.dateEmission.toDate() : latest, null
        ) : null
    };

  } catch (error) {
    console.error('Erreur lors de la détermination du statut client:', error);
    throw error;
  }
};

/**
 * Met à jour le statut d'un client
 * @param {Object} db - Instance Firestore
 * @param {string} clientId - ID du client
 * @param {string} nouveauStatut - Nouveau statut
 * @param {Object} metadata - Métadonnées supplémentaires
 * @returns {Promise<void>}
 */
export const mettreAJourStatutClient = async (db, clientId, nouveauStatut, metadata = {}) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    
    const updateData = {
      statutClient: nouveauStatut,
      // Maintenir la compatibilité avec l'ancien système
      isActive: nouveauStatut === CLIENT_STATUS.CLIENT_ACTIF,
      dateModificationStatut: Timestamp.now(),
      ...metadata
    };

    await updateDoc(clientRef, updateData);
    
    console.log(`Statut client ${clientId} mis à jour vers: ${nouveauStatut}`);
    
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut client:', error);
    throw error;
  }
};

/**
 * Convertit automatiquement un prospect en client actif lors de l'acceptation d'un devis
 * @param {Object} db - Instance Firestore
 * @param {string} clientId - ID du client
 * @param {string} devisId - ID du devis accepté
 * @returns {Promise<void>}
 */
export const convertirProspectEnClient = async (db, clientId, devisId) => {
  try {
    console.log(`🔄 Conversion prospect → client pour: ${clientId} (devis: ${devisId})`);
    
    await mettreAJourStatutClient(db, clientId, CLIENT_STATUS.CLIENT_ACTIF, {
      premierDevisAccepte: devisId,
      dateConversionClient: Timestamp.now(),
      raisonConversion: 'Acceptation de devis'
    });
    
    console.log(`✅ Prospect ${clientId} converti en client actif`);
    
  } catch (error) {
    console.error('Erreur lors de la conversion prospect → client:', error);
    throw error;
  }
};

/**
 * Obtient tous les prospects avec leur pipeline de conversion
 * @param {Object} db - Instance Firestore
 * @returns {Promise<Array>} Liste des prospects avec métadonnées
 */
export const obtenirPipelineProspects = async (db) => {
  try {
    // Récupérer tous les prospects
    const prospectsQuery = query(
      collection(db, 'clients'),
      where('statutClient', '==', CLIENT_STATUS.PROSPECT)
    );
    const prospectsSnapshot = await getDocs(prospectsQuery);
    const prospects = prospectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Enrichir chaque prospect avec ses données de pipeline
    const prospectsPipeline = await Promise.all(
      prospects.map(async (prospect) => {
        const statusInfo = await determinerStatutClient(db, prospect.id);
        
        // Récupérer le dernier devis
        const devisQuery = query(
          collection(db, 'devis'),
          where('clientId', '==', prospect.id),
          where('statut', 'in', ['envoye', 'accepte', 'refuse'])
        );
        const devisSnapshot = await getDocs(devisQuery);
        const devis = devisSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => b.dateCreation?.toDate() - a.dateCreation?.toDate());

        const dernierDevis = devis[0] || null;

        return {
          ...prospect,
          pipeline: {
            ...statusInfo,
            dernierDevis,
            prochaineSuggestion: suggererProchaineAction(prospect, dernierDevis)
          }
        };
      })
    );

    return prospectsPipeline;

  } catch (error) {
    console.error('Erreur lors de la récupération du pipeline prospects:', error);
    throw error;
  }
};

/**
 * Suggère la prochaine action pour un prospect
 * @param {Object} prospect - Données du prospect
 * @param {Object} dernierDevis - Dernier devis du prospect
 * @returns {Object} Suggestion d'action
 */
const suggererProchaineAction = (prospect, dernierDevis) => {
  if (!dernierDevis) {
    return {
      action: 'creer_devis',
      label: 'Créer un premier devis',
      priorite: 'haute',
      icon: '📄'
    };
  }

  const maintenant = new Date();
  const dateDevis = dernierDevis.dateCreation?.toDate() || new Date(0);
  const joursDepuis = Math.floor((maintenant - dateDevis) / (24 * 60 * 60 * 1000));

  switch (dernierDevis.statut) {
    case 'envoye':
      if (joursDepuis > 7) {
        return {
          action: 'relancer_prospect',
          label: `Relancer (${joursDepuis}j sans réponse)`,
          priorite: 'haute',
          icon: '📞'
        };
      }
      return {
        action: 'attendre_reponse',
        label: `En attente de réponse (${joursDepuis}j)`,
        priorite: 'normale',
        icon: '⏳'
      };

    case 'refuse':
      if (joursDepuis > 30) {
        return {
          action: 'nouveau_devis',
          label: 'Proposer un nouveau devis',
          priorite: 'normale',
          icon: '🔄'
        };
      }
      return {
        action: 'analyser_refus',
        label: 'Analyser les raisons du refus',
        priorite: 'basse',
        icon: '🔍'
      };

    case 'accepte':
      return {
        action: 'convertir_client',
        label: 'Finaliser la conversion en client',
        priorite: 'critique',
        icon: '✅'
      };

    default:
      return {
        action: 'evaluer_situation',
        label: 'Évaluer la situation',
        priorite: 'normale',
        icon: '❓'
      };
  }
};

/**
 * Migration automatique des anciens clients vers le nouveau système
 * @param {Object} db - Instance Firestore
 * @returns {Promise<Object>} Résultat de la migration
 */
export const migrerAnciensClients = async (db) => {
  try {
    console.log('🔄 Début de la migration des anciens clients...');
    
    // Récupérer tous les clients sans statutClient
    const clientsQuery = query(
      collection(db, 'clients'),
      where('statutClient', '==', null)
    );
    const clientsSnapshot = await getDocs(clientsQuery);
    const clientsAMigrer = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    let migres = 0;
    let erreurs = 0;

    for (const client of clientsAMigrer) {
      try {
        const statusInfo = await determinerStatutClient(db, client.id);
        await mettreAJourStatutClient(db, client.id, statusInfo.statutRecommande, {
          migrationAutomatique: true,
          ancienIsActive: client.isActive
        });
        migres++;
      } catch (error) {
        console.error(`Erreur migration client ${client.id}:`, error);
        erreurs++;
      }
    }

    console.log(`✅ Migration terminée: ${migres} clients migrés, ${erreurs} erreurs`);
    
    return {
      totalClients: clientsAMigrer.length,
      migres,
      erreurs,
      success: erreurs === 0
    };

  } catch (error) {
    console.error('Erreur lors de la migration des anciens clients:', error);
    throw error;
  }
};

export default {
  CLIENT_STATUS,
  CLIENT_STATUS_LABELS,
  CLIENT_STATUS_COLORS,
  convertirAncienStatut,
  determinerStatutClient,
  mettreAJourStatutClient,
  convertirProspectEnClient,
  obtenirPipelineProspects,
  migrerAnciensClients
};
